import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BookOpen, Star, Heart, Share2, Bookmark, BookmarkCheck, Tag, CheckCircle, RefreshCw, Settings, Filter, Search, Plus } from 'lucide-react';

interface RelatedArticle {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  authorId: string;
  publishedAt: Date;
  readTime: number;
  imageUrl?: string;
  tags: string[];
  category: string;
  relevance: number;
  similarity: number;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  isBookmarked: boolean;
  isLiked: boolean;
  isRead: boolean;
  isRecommended: boolean;
  recommendationReason: string;
  source: 'ai' | 'user' | 'trending' | 'similar';
}

interface RelatedTopic {
  id: string;
  name: string;
  description: string;
  articleCount: number;
  trendingScore: number;
  category: string;
  tags: string[];
  isFollowing: boolean;
  isRecommended: boolean;
  lastUpdated: Date;
}

interface RelatedContentProps {
  articleId: string;
  articleTitle: string;
  articleTags: string[];
  articleCategory: string;
  onArticleClick?: (article: RelatedArticle) => void;
  onTopicClick?: (topic: RelatedTopic) => void;
  onBookmark?: (articleId: string, bookmarked: boolean) => void;
  onLike?: (articleId: string, liked: boolean) => void;
  className?: string;
}

const RelatedContent: React.FC<RelatedContentProps> = ({
  articleId,
  articleTitle,
  articleTags,
  articleCategory,
  onArticleClick,
  onTopicClick,
  onBookmark,
  onLike,
  className = ''
}) => {
  const [relatedArticles, setRelatedArticles] = useState<RelatedArticle[]>([]);
  const [relatedTopics, setRelatedTopics] = useState<RelatedTopic[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showArticles, setShowArticles] = useState(true);
  const [showTopics, setShowTopics] = useState(true);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'relevance' | 'trending' | 'recent' | 'popular'>('relevance');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Mock data
  useEffect(() => {
    const mockArticles: RelatedArticle[] = [
      {
        id: '1',
        title: 'Understanding React Server Components: A Complete Guide',
        excerpt: 'Dive deep into React Server Components and learn how they revolutionize web development with better performance and SEO.',
        author: 'Sarah Chen',
        authorId: 'author1',
        publishedAt: new Date('2024-01-15'),
        readTime: 12,
        imageUrl: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=200&fit=crop',
        tags: ['react', 'server-components', 'web-development', 'performance'],
        category: 'Web Development',
        relevance: 0.95,
        similarity: 0.88,
        views: 15420,
        likes: 234,
        comments: 45,
        shares: 67,
        isBookmarked: false,
        isLiked: true,
        isRead: false,
        isRecommended: true,
        recommendationReason: 'Highly similar to your current article',
        source: 'ai'
      },
      {
        id: '2',
        title: 'WebAssembly: The Future of Web Performance',
        excerpt: 'Explore how WebAssembly is changing the web development landscape and enabling new possibilities for high-performance applications.',
        author: 'Mike Johnson',
        authorId: 'author2',
        publishedAt: new Date('2024-01-10'),
        readTime: 8,
        imageUrl: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=200&fit=crop',
        tags: ['webassembly', 'performance', 'web-development', 'wasm'],
        category: 'Web Development',
        relevance: 0.87,
        similarity: 0.82,
        views: 8930,
        likes: 156,
        comments: 23,
        shares: 34,
        isBookmarked: true,
        isLiked: false,
        isRead: true,
        isRecommended: true,
        recommendationReason: 'Covers similar performance topics',
        source: 'ai'
      },
      {
        id: '3',
        title: 'Edge Computing: The Next Frontier in Web Architecture',
        excerpt: 'Learn about edge computing and how it\'s reshaping web architecture for better performance and user experience.',
        author: 'Alex Rodriguez',
        authorId: 'author3',
        publishedAt: new Date('2024-01-05'),
        readTime: 10,
        imageUrl: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=200&fit=crop',
        tags: ['edge-computing', 'architecture', 'performance', 'cloud'],
        category: 'Web Development',
        relevance: 0.79,
        similarity: 0.75,
        views: 6780,
        likes: 98,
        comments: 18,
        shares: 29,
        isBookmarked: false,
        isLiked: false,
        isRead: false,
        isRecommended: false,
        recommendationReason: 'Related to web performance',
        source: 'similar'
      }
    ];

    const mockTopics: RelatedTopic[] = [
      {
        id: '1',
        name: 'React Server Components',
        description: 'Latest developments in React Server Components and their impact on web development',
        articleCount: 24,
        trendingScore: 0.92,
        category: 'Web Development',
        tags: ['react', 'server-components', 'web-development'],
        isFollowing: false,
        isRecommended: true,
        lastUpdated: new Date('2024-01-20')
      },
      {
        id: '2',
        name: 'Web Performance',
        description: 'Techniques and tools for optimizing web application performance',
        articleCount: 156,
        trendingScore: 0.88,
        category: 'Web Development',
        tags: ['performance', 'optimization', 'web-development'],
        isFollowing: true,
        isRecommended: true,
        lastUpdated: new Date('2024-01-18')
      },
      {
        id: '3',
        name: 'Modern Web Architecture',
        description: 'Exploring new architectural patterns and technologies in web development',
        articleCount: 89,
        trendingScore: 0.85,
        category: 'Web Development',
        tags: ['architecture', 'web-development', 'patterns'],
        isFollowing: false,
        isRecommended: false,
        lastUpdated: new Date('2024-01-15')
      }
    ];

    setRelatedArticles(mockArticles);
    setRelatedTopics(mockTopics);
  }, [articleId, articleTitle, articleTags, articleCategory]);

  // Load more content
  const loadMoreContent = async () => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Add more mock articles
    const newArticles: RelatedArticle[] = [
      {
        id: '4',
        title: 'AI-Powered Development Tools: The Future is Here',
        excerpt: 'Discover how AI is revolutionizing the development process with intelligent coding assistants and automated tools.',
        author: 'Emma Wilson',
        authorId: 'author4',
        publishedAt: new Date('2024-01-12'),
        readTime: 9,
        imageUrl: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=200&fit=crop',
        tags: ['ai', 'development-tools', 'automation', 'productivity'],
        category: 'Web Development',
        relevance: 0.72,
        similarity: 0.68,
        views: 4560,
        likes: 78,
        comments: 12,
        shares: 19,
        isBookmarked: false,
        isLiked: false,
        isRead: false,
        isRecommended: false,
        recommendationReason: 'Trending in web development',
        source: 'trending'
      }
    ];
    
    setRelatedArticles(prev => [...prev, ...newArticles]);
    setIsLoading(false);
  };

  // Handle article actions
  const handleBookmark = (articleId: string, bookmarked: boolean) => {
    setRelatedArticles(prev => prev.map(article => 
      article.id === articleId 
        ? { ...article, isBookmarked: bookmarked }
        : article
    ));
    onBookmark?.(articleId, bookmarked);
  };

  const handleLike = (articleId: string, liked: boolean) => {
    setRelatedArticles(prev => prev.map(article => 
      article.id === articleId 
        ? { ...article, isLiked: liked, likes: liked ? article.likes + 1 : article.likes - 1 }
        : article
    ));
    onLike?.(articleId, liked);
  };

  const handleTopicFollow = (topicId: string, following: boolean) => {
    setRelatedTopics(prev => prev.map(topic => 
      topic.id === topicId 
        ? { ...topic, isFollowing: following }
        : topic
    ));
  };

  // Filter and sort articles
  const filteredArticles = relatedArticles
    .filter(article => {
      const matchesCategory = filterCategory === 'all' || article.category === filterCategory;
      const matchesSearch = searchQuery === '' || 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'relevance':
          return b.relevance - a.relevance;
        case 'trending':
          return b.views - a.views;
        case 'recent':
          return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
        case 'popular':
          return b.likes - a.likes;
        default:
          return 0;
      }
    });

  const filteredTopics = relatedTopics
    .filter(topic => {
      const matchesSearch = searchQuery === '' || 
        topic.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        topic.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        topic.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesSearch;
    })
    .sort((a, b) => b.trendingScore - a.trendingScore);

  return (
    <div className={`related-content ${className}`}>
      {/* Related Content Panel */}
      <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg z-40">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-green-500" />
              Related Content
            </h3>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search related content..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
          
          {/* Filters */}
          <div className="flex gap-2 mb-4">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">All Categories</option>
              <option value="Web Development">Web Development</option>
              <option value="AI">AI</option>
              <option value="Design">Design</option>
              <option value="Business">Business</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="relevance">Relevance</option>
              <option value="trending">Trending</option>
              <option value="recent">Recent</option>
              <option value="popular">Popular</option>
            </select>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowArticles(!showArticles)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                showArticles 
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Articles ({filteredArticles.length})
            </button>
            
            <button
              onClick={() => setShowTopics(!showTopics)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                showTopics 
                  ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Tag className="w-4 h-4" />
              Topics ({filteredTopics.length})
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {/* Related Articles */}
          {showArticles && (
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Related Articles
              </h4>
              
              {filteredArticles.map(article => (
                <div
                  key={article.id}
                  className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  onClick={() => onArticleClick?.(article)}
                >
                  <div className="flex items-start gap-3">
                    {article.imageUrl && (
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                      />
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-medium text-gray-900 dark:text-white text-sm line-clamp-2">
                          {article.title}
                        </h5>
                        <div className="flex items-center gap-1 ml-2">
                          {article.isRecommended && (
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                          )}
                          {article.isRead && (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                        {article.excerpt}
                      </p>
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <span>{article.author}</span>
                        <span>•</span>
                        <span>{article.readTime}m read</span>
                        <span>•</span>
                        <span>{new Date(article.publishedAt).toLocaleDateString()}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <span>{article.views} views</span>
                        <span>•</span>
                        <span>{article.likes} likes</span>
                        <span>•</span>
                        <span>{article.comments} comments</span>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full">
                          {article.category}
                        </span>
                        <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                          {Math.round(article.relevance * 100)}% relevant
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBookmark(article.id, !article.isBookmarked);
                          }}
                          className={`p-1 rounded transition-colors ${
                            article.isBookmarked 
                              ? 'text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20' 
                              : 'text-gray-400 hover:text-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          {article.isBookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLike(article.id, !article.isLiked);
                          }}
                          className={`p-1 rounded transition-colors ${
                            article.isLiked 
                              ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20' 
                              : 'text-gray-400 hover:text-red-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${article.isLiked ? 'fill-current' : ''}`} />
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(window.location.origin + '/article/' + article.id);
                          }}
                          className="p-1 text-gray-400 hover:text-green-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {filteredArticles.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No related articles found</p>
                  <p className="text-sm">Try adjusting your search or filters</p>
                </div>
              )}
            </div>
          )}
          
          {/* Related Topics */}
          {showTopics && (
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Related Topics
              </h4>
              
              {filteredTopics.map(topic => (
                <div
                  key={topic.id}
                  className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  onClick={() => onTopicClick?.(topic)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h5 className="font-medium text-gray-900 dark:text-white text-sm">
                      {topic.name}
                    </h5>
                    <div className="flex items-center gap-1">
                      {topic.isRecommended && (
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTopicFollow(topic.id, !topic.isFollowing);
                        }}
                        className={`p-1 rounded transition-colors ${
                          topic.isFollowing 
                            ? 'text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20' 
                            : 'text-gray-400 hover:text-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {topic.isFollowing ? <CheckCircle className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                    {topic.description}
                  </p>
                  
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <span>{topic.articleCount} articles</span>
                    <span>•</span>
                    <span>{Math.round(topic.trendingScore * 100)}% trending</span>
                    <span>•</span>
                    <span>{topic.category}</span>
                  </div>
                  
                  <div className="flex flex-wrap gap-1">
                    {topic.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              
              {filteredTopics.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Tag className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No related topics found</p>
                  <p className="text-sm">Try adjusting your search or filters</p>
                </div>
              )}
            </div>
          )}
          
          {/* Load More Button */}
          <div className="mt-6 text-center">
            <button
              onClick={loadMoreContent}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                  Loading...
                </>
              ) : (
                'Load More Content'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelatedContent;
